import React, { useEffect, useState } from 'react';
import {
  Alert, AlertIcon, Box, Button, Flex, Grid, Heading, HStack, Link, SimpleGrid,
  Skeleton, Stack, Switch, Text,
} from '@chakra-ui/react';
import { fetchWithAuth } from '../../utils/fetchWithAuth';

// Marks follow what the person did; stages use one blue ramp, light to dark.
const MARK = { plan: '#718096', returned: '#2A78D6', changed: '#1BAF7A', build: '#EB6834' };
const STEP = ['#86B6EF', '#6DA7EC', '#5598E7', '#3987E5', '#2A78D6', '#1C5CAB', '#184F95'];
const STATE = {
  not_yet: { label: 'Not yet', colour: '#A0AEC0' },
  starting: { label: 'Starting', colour: '#FAB219' },
  proven_stalled: { label: 'Proven for a few, stalled', colour: '#FAB219' },
  proven_active: { label: 'Proven for a few, active', colour: '#0CA30C' },
};
const GROUPS = [
  ['in_loop', 'In the loop', 'Closed the loop 2 or more times'],
  ['one_loop', 'Closed the loop once', 'Brought something back and acted on it, one time'],
  ['came_back', 'Came back, no loop closed', 'Returned, but nothing they brought back was reviewed'],
  ['once', 'Used once', 'One day, then gone'],
  ['never_started', 'Never started', 'No strategy yet'],
];

const DAY = 86400000;
const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`;
const formatDate = value => new Date(value).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric' });
// en-US prints "NZ$", so New Zealand dollars never read as US dollars next to the US$ benchmark.
const nzd = value => {
  const digits = value < 100 ? 2 : 0;
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'NZD', minimumFractionDigits: digits, maximumFractionDigits: digits }).format(value);
};
const daysBetween = (from, to) => Math.max(0, Math.round((new Date(to) - new Date(from)) / DAY));

// One floating tooltip for every mark, so hundreds of marks stay cheap.
function useHoverTip() {
  const [tip, setTip] = useState(null);
  const handlers = {
    onMouseOver: event => {
      const target = event.target.closest?.('[data-tip]');
      if (!target) { setTip(null); return; }
      const [title, ...rest] = target.getAttribute('data-tip').split('|');
      setTip({ title, body: rest.join('|'), x: event.clientX, y: event.clientY });
    },
    onMouseMove: event => setTip(current => (current ? { ...current, x: event.clientX, y: event.clientY } : current)),
    onMouseLeave: () => setTip(null),
  };
  const node = tip && <Box position="fixed" left={`${Math.min(tip.x + 12, window.innerWidth - 330)}px`} top={`${tip.y + 14}px`}
    zIndex={20} pointerEvents="none" bg="gray.800" color="white" px={3} py={2} borderRadius="md" fontSize="xs" maxW="320px" role="tooltip">
    <Text fontWeight="semibold">{tip.title}</Text>{tip.body && <Text>{tip.body}</Text>}
  </Box>;
  return [handlers, node];
}

function Section({ title, intro, children }) {
  return <Box as="section" mt={8} pt={6} borderTopWidth="1px" borderColor="gray.200">
    <Heading as="h3" fontSize="md" fontWeight="semibold">{title}</Heading>
    {intro && <Text fontSize="sm" color="gray.600" mt={1} mb={4}>{intro}</Text>}
    {children}
  </Box>;
}

function Answer({ answer, generatedAt }) {
  const state = STATE[answer.state] || STATE.not_yet;
  const verdict = answer.state === 'not_yet' ? 'Not yet. Nobody has closed the loop.'
    : answer.state === 'starting' ? `Starting. ${plural(answer.once, 'person', 'people')} closed the loop once.`
      : `Yes, for ${plural(answer.repeated, 'person', 'people')}. ${answer.recent ? `${answer.recent} still doing it.` : 'It has stalled.'}`;
  const parts = [];
  if (answer.repeated) parts.push(`${answer.repeated} of ${answer.total} people keep bringing back what happened when they act, and change their decisions because of it.`);
  if (answer.once) parts.push(`${plural(answer.once, 'more person', 'more people')} did it once.`);
  parts.push(`${answer.never_returned} never brought anything back.`);
  if (answer.last_loop) parts.push(`Last loop closed: ${answer.last_loop.name}, ${formatDate(answer.last_loop.at)} (${daysBetween(answer.last_loop.at, generatedAt)} days ago).`);
  return <Box mt={5} p={5} bg="gray.50" borderWidth="1px" borderColor="gray.100" borderRadius="md" aria-live="polite">
    <HStack spacing={2}>
      <Box w="10px" h="10px" borderRadius="full" bg={state.colour} aria-hidden="true" />
      <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider" color="gray.700">{state.label}</Text>
    </HStack>
    <Text as="p" fontSize={{ base: '2xl', md: '3xl' }} fontWeight="bold" lineHeight="1.2" mt={2} mb={2}>{verdict}</Text>
    <Text color="gray.700" maxW="760px">{parts.join(' ')}</Text>
  </Box>;
}

function Staircase({ steps, people, tipHandlers }) {
  return <Grid templateColumns={{ base: '1fr', md: 'minmax(170px, 240px) 1fr' }} columnGap={4} rowGap={{ base: 1, md: 2 }} alignItems="center" {...tipHandlers}>
    {steps.map((step, index) => <React.Fragment key={step.key}>
      {index === 3 && <Flex gridColumn="1 / -1" align="center" gap={2} mt={2} fontSize="xs" color="gray.600">
        <Text>The loop</Text><Box flex="1" h="1px" bg="gray.200" />
      </Flex>}
      <Text fontSize="sm" mt={{ base: 2, md: 0 }}>{step.label}</Text>
      <Flex align="center" gap={3} wrap="wrap" minW={0}>
        <Flex gap={{ base: '3px', md: '4px' }} wrap="wrap" maxW="100%" role="img" aria-label={`${step.label}: ${step.count} of ${people.length} people`}>
          {people.map(person => {
            const on = person.depth > index;
            return <Box key={person.id} w={{ base: '8px', md: '14px' }} h={{ base: '8px', md: '14px' }} borderRadius="full" flexShrink={0}
              bg={on ? STEP[index] : 'gray.200'} boxShadow={person.is_admin && on ? `inset 0 0 0 2px white, 0 0 0 1.5px ${STEP[index]}` : undefined}
              data-tip={`${person.name}${person.is_admin ? ' (admin)' : ''}|${on ? step.label : `Did not reach: ${step.label}`}`} />;
          })}
        </Flex>
        <Text fontSize="sm"><Text as="b" sx={{ fontVariantNumeric: 'tabular-nums' }}>{step.count}</Text> of {people.length}</Text>
        {index > 0 && step.lost > 0 && step.lost_label && <Text fontSize="sm" color="gray.600">{step.lost} {step.lost_label}</Text>}
      </Flex>
    </React.Fragment>)}
  </Grid>;
}

function ShareCell({ count, total, suffix }) {
  const percent = total ? count / total * 100 : 0;
  return <Box>
    <Text fontSize="sm"><Text as="b">{count}</Text> of {total}{suffix ? ` ${suffix}` : ''}</Text>
    <Box h="6px" bg="gray.100" borderRadius="sm" mt={1} overflow="hidden" aria-hidden="true">
      <Box h="100%" w={`${percent}%`} bg="#2A78D6" borderRadius="sm" />
    </Box>
  </Box>;
}

function Worth({ value }) {
  const { looped, others } = value.groups;
  const { totals, assumptions } = value;
  const max = Math.max(1, ...value.changes.map(item => item.count));
  const scale = Math.max(1, totals.usual_high_nzd);
  const rows = [
    ['Would pay for Pabs', 'would_pay', 'people'],
    ['Paid for credits or Pro', 'paid', 'people'],
    ['Rated Pabs essential, 4 or 5 of 5', 'essential', 'rated'],
  ];
  if (!value.loops) {
    return <Text fontSize="sm" color="gray.600">No closed loops yet, so there is nothing to value. Once someone brings back what happened and reviews it, it shows here.</Text>;
  }
  return <Stack spacing={8}>
    <Box>
      <Text fontWeight="semibold" fontSize="sm" mb={3}>People who closed a loop, compared with everyone else</Text>
      <Grid templateColumns={{ base: '1fr 1fr', md: 'minmax(180px, 260px) 1fr 1fr' }} columnGap={6} rowGap={3} maxW="760px">
        <Box display={{ base: 'none', md: 'block' }} />
        <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider" color="gray.600">Closed a loop ({looped.people})</Text>
        <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider" color="gray.600">Everyone else ({others.people})</Text>
        {rows.map(([label, key, base]) => <React.Fragment key={key}>
          <Text gridColumn={{ base: '1 / -1', md: 'auto' }} fontSize="sm" color="gray.700">{label}</Text>
          <ShareCell count={looped[key]} total={looped[base]} suffix={base === 'rated' ? 'who answered' : ''} />
          <ShareCell count={others[key]} total={others[base]} suffix={base === 'rated' ? 'who answered' : ''} />
        </React.Fragment>)}
        <Text gridColumn={{ base: '1 / -1', md: 'auto' }} fontSize="sm" color="gray.700">Median credits used</Text>
        <Text fontSize="sm"><Text as="b">{looped.median_credits ?? 0}</Text></Text>
        <Text fontSize="sm"><Text as="b">{others.median_credits ?? 0}</Text></Text>
      </Grid>
    </Box>

    <Box>
      <Text fontWeight="semibold" fontSize="sm" mb={1}>What {plural(totals.loops, 'loop', 'loops')} changed in their products</Text>
      <Text fontSize="sm" color="gray.600" mb={3}>{value.from_build} came straight from a build tool reporting back while they built.</Text>
      <Stack spacing={2} maxW="760px">
        {value.changes.map(item => <Grid key={item.key} templateColumns={{ base: '1fr auto', md: 'minmax(180px, 300px) 1fr auto' }} columnGap={3} alignItems="center">
          <Text fontSize="sm">{item.label}</Text>
          <Box display={{ base: 'none', md: 'block' }} h="10px" bg="gray.100" borderRadius="sm" overflow="hidden" aria-hidden="true">
            <Box h="100%" w={`${item.count / max * 100}%`} bg={item.key === 'kept' ? MARK.plan : MARK.changed} borderRadius="sm" />
          </Box>
          <Text fontSize="sm" fontWeight="bold" sx={{ fontVariantNumeric: 'tabular-nums' }}>{item.count}</Text>
        </Grid>)}
      </Stack>
    </Box>

    <Box>
      <Text fontWeight="semibold" fontSize="sm" mb={1}>The same work, the usual way</Text>
      <Text fontSize="sm" color="gray.600" mb={4}>Estimate. {plural(totals.loops, 'reviewed decision', 'reviewed decisions')}, if each had been a session with a {assumptions.role}.</Text>
      <Stack spacing={3} maxW="760px">
        <Grid templateColumns={{ base: '1fr', md: 'minmax(180px, 220px) 1fr' }} columnGap={4} rowGap={1} alignItems="center">
          <Text fontSize="sm">In Pabs</Text>
          <Flex align="center" gap={3}>
            <Box h="14px" w={`${Math.max(0.6, (totals.pabs_cost_nzd || 0) / scale * 100)}%`} minW="4px" bg="#2A78D6" borderRadius="sm" />
            <Text fontSize="sm" fontWeight="bold">{totals.pabs_cost_nzd === null ? 'Price unknown' : nzd(totals.pabs_cost_nzd)}</Text>
          </Flex>
          <Text fontSize="sm">The usual way</Text>
          <Flex align="center" gap={3}>
            <Box position="relative" h="14px" flex="1" aria-hidden="true">
              <Box position="absolute" inset={0} w="100%" bg="#B7D3F6" borderRadius="sm" />
              <Box position="absolute" top={0} bottom={0} left={0} w={`${totals.usual_low_nzd / scale * 100}%`} bg="#1C5CAB" borderRadius="sm" />
            </Box>
            <Text fontSize="sm" fontWeight="bold" whiteSpace="nowrap">{nzd(totals.usual_low_nzd)} to {nzd(totals.usual_high_nzd)}</Text>
          </Flex>
        </Grid>
        <Stack spacing={1} pt={2}>
          {value.replaced.map(item => <Flex key={item.person_id} justify="space-between" gap={3} fontSize="sm" color="gray.700" wrap="wrap">
            <Text>{item.name}{item.is_admin ? ' · admin' : ''} · {plural(item.loops, 'loop', 'loops')}</Text>
            <Text sx={{ fontVariantNumeric: 'tabular-nums' }}>{item.pabs_cost_nzd === null ? 'Price unknown' : nzd(item.pabs_cost_nzd)} in Pabs, {nzd(item.usual_low_nzd)} to {nzd(item.usual_high_nzd)} the usual way</Text>
          </Flex>)}
        </Stack>
        {value.speed.measured > 0 && <Text fontSize="sm" color="gray.700">
          {value.speed.same_day} of {value.speed.measured} decisions were made the same day the evidence came back. The usual way waits for the next session.
        </Text>}
        <Text fontSize="xs" color="gray.600">
          Assumes {assumptions.hours_low} to {assumptions.hours_high} hours of a {assumptions.role} per reviewed decision at US${assumptions.usd_per_hour} an hour, converted at {assumptions.usd_to_nzd} NZD per USD.
          {' '}Pabs cost is every credit they used, including scheduled Pulses, {assumptions.nzd_per_credit ? `at the Pro list price of ${nzd(assumptions.nzd_per_credit)} a credit` : 'but the Pro price could not be read'}. This is time the work would take, not money anyone saved.
        </Text>
      </Stack>
    </Box>

    <Box>
      <Text fontWeight="semibold" fontSize="sm" mb={1}>Why changing course early matters</Text>
      <Text fontSize="sm" color="gray.700" maxW="760px">
        Software projects spend 40 to 50 percent of their effort on avoidable rework, and 80 percent of features are rarely or never used.
        {' '}{plural(value.from_build, 'loop', 'loops')} here changed a decision during the build, before more was built on it.
      </Text>
      <Stack as="ol" spacing={1} mt={3} pl={5} fontSize="xs" color="gray.600">
        {value.sources.map(source => <Text as="li" key={source.key}>
          <Link href={source.url} isExternal color="blue.700" textDecoration="underline">{source.label}</Link>
        </Text>)}
      </Stack>
    </Box>
  </Stack>;
}

const RANGES = [
  { key: 'all', label: 'All', days: null, phrase: 'all time' },
  { key: '1y', label: '1 year', days: 365, phrase: 'the last year' },
  { key: '6m', label: '6 months', days: 183, phrase: 'the last 6 months' },
  { key: '3m', label: '3 months', days: 91, phrase: 'the last 3 months' },
  { key: '1m', label: '1 month', days: 30, phrase: 'the last month' },
];

function RangeSwitch({ value, onChange }) {
  return <Flex role="group" aria-label="Calendar range" wrap="wrap" gap={2}>
    {RANGES.map(range => {
      const selected = value === range.key;
      return <Button key={range.key} size="sm" minH={{ base: '44px', md: '32px' }} variant="outline" aria-pressed={selected}
        onClick={() => onChange(range.key)} borderColor={selected ? 'gray.700' : 'gray.300'}
        bg={selected ? 'gray.700' : 'white'} color={selected ? 'white' : 'gray.700'} _hover={{ bg: selected ? 'gray.600' : 'gray.50' }}>
        {range.label}
      </Button>;
    })}
  </Flex>;
}

// Month ticks for long ranges, Monday ticks for short ones; labels thin out so they never collide.
function calendarTicks(startAt, endAt, x) {
  const ticks = [];
  if (endAt - startAt > 120 * DAY) {
    const month = new Date(startAt); month.setUTCDate(1); month.setUTCHours(0, 0, 0, 0);
    if (month.getTime() < startAt) month.setUTCMonth(month.getUTCMonth() + 1);
    for (; month.getTime() <= endAt; month.setUTCMonth(month.getUTCMonth() + 1)) {
      ticks.push({ at: month.getTime(), label: month.toLocaleDateString('en-NZ', { month: 'short', timeZone: 'UTC' }), january: month.getUTCMonth() === 0 });
    }
  } else {
    const monday = new Date(startAt); monday.setUTCHours(0, 0, 0, 0);
    monday.setUTCDate(monday.getUTCDate() + ((8 - monday.getUTCDay()) % 7));
    for (; monday.getTime() <= endAt; monday.setUTCDate(monday.getUTCDate() + 7)) {
      ticks.push({ at: monday.getTime(), label: monday.toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', timeZone: 'UTC' }), january: false });
    }
  }
  const spacing = ticks.length > 1 ? x(ticks[1].at) - x(ticks[0].at) : Infinity;
  const every = Math.max(1, Math.ceil(46 / spacing));
  let yearShown = false;
  return ticks.map((tick, index) => {
    const labelled = index % every === 0;
    const year = labelled && (tick.january || !yearShown) ? new Date(tick.at).getUTCFullYear() : null;
    if (year) yearShown = true;
    return { ...tick, labelled, year };
  });
}

function Timeline({ people, generatedAt, range, tipHandlers }) {
  const nowAt = new Date(generatedAt).getTime();
  const days = RANGES.find(item => item.key === range)?.days ?? null;
  const firsts = people.map(person => person.first_at).filter(Boolean).sort();
  const firstMonth = new Date(firsts[0] || generatedAt); firstMonth.setUTCDate(1); firstMonth.setUTCHours(0, 0, 0, 0);
  // Marks are whole days, so a ranged view starts at midnight and never draws left of the plot.
  const startAt = days ? Math.floor((nowAt - days * DAY) / DAY) * DAY : firstMonth.getTime();
  const span = Math.max(DAY, nowAt - startAt), endAt = nowAt + span * 0.03;
  const L = 200, R = 1000, W = 1075, rowH = 24;
  const x = value => L + (new Date(value).getTime() - startAt) / (endAt - startAt) * (R - L);
  const inRange = date => { const at = new Date(date).getTime(); return at >= startAt && at <= endAt; };
  const bandWidth = Math.max(6, (R - L) * DAY / (endAt - startAt) * 1.2);
  const order = { plan: 0, build: 1, returned: 2, changed: 3 };
  const parts = [];
  let y = 40;
  for (const [key, title, sub] of GROUPS) {
    const rows = people.filter(person => person.stage === key);
    if (!rows.length) continue;
    const shown = days ? rows.filter(person => person.marks.some(mark => inRange(mark.date))) : rows;
    parts.push(<text key={`${key}-title`} x="0" y={y + 14} fontSize="12" fontWeight="600" fill="#1A202C">{title} · {rows.length}<tspan dx="10" fontWeight="400" fill="#718096" fontSize="11">{sub}</tspan></text>);
    y += 26;
    for (const person of shown) {
      const top = y, cy = top + rowH / 2;
      parts.push(<g key={person.id}>
        <title>{`${person.name}: ${plural(person.loops, 'loop', 'loops')}${person.last_seen_at ? `, last seen ${formatDate(person.last_seen_at)}` : ''}`}</title>
        <line x1={L} x2={R} y1={top + rowH} y2={top + rowH} stroke="#EDF2F7" />
        <text x="0" y={cy + 4} fontSize="12" fill="#4A5568">{person.name}{person.is_admin ? ' · admin' : ''}</text>
        {person.loop_dates.filter(inRange).map((date, index) => <rect key={`loop-${index}`} x={x(date) - bandWidth / 2} y={top + 2} width={bandWidth} height={rowH - 4} rx="2" fill="rgba(27,175,122,0.14)" />)}
        {person.marks.filter(mark => inRange(mark.date)).sort((a, b) => order[a.kind] - order[b.kind]).map(mark => {
          const cx = x(mark.date), tip = `${formatDate(mark.date)}|${mark.items.join('; ')}`;
          if (mark.kind === 'plan') return <circle key={`${mark.date}-plan`} cx={cx} cy={cy} r="3.5" fill={MARK.plan} data-tip={tip} />;
          if (mark.kind === 'returned') return <circle key={`${mark.date}-returned`} cx={cx} cy={cy} r="5" fill={MARK.returned} stroke="white" strokeWidth="1.5" data-tip={tip} />;
          if (mark.kind === 'changed') return <path key={`${mark.date}-changed`} d={`M${cx} ${cy - 6}L${cx + 6} ${cy}L${cx} ${cy + 6}L${cx - 6} ${cy}Z`} fill={MARK.changed} stroke="white" strokeWidth="1.5" data-tip={tip} />;
          return <path key={`${mark.date}-build`} d={`M${cx} ${cy - 6}L${cx + 6} ${cy + 5}L${cx - 6} ${cy + 5}Z`} fill={MARK.build} stroke="white" strokeWidth="1.5" data-tip={tip} />;
        })}
        <text x={R + 8} y={cy + 4} fontSize="12" fill="#718096">{person.last_seen_at ? `${daysBetween(person.last_seen_at, generatedAt)}d ago` : 'never'}</text>
      </g>);
      y += rowH;
    }
    const quiet = rows.length - shown.length;
    if (quiet) {
      parts.push(<text key={`${key}-quiet`} x="0" y={y + 15} fontSize="12" fill="#718096" fontStyle="italic">
        {shown.length ? `${plural(quiet, 'more person', 'more people')} with nothing in this period` : `Nothing from ${quiet === 1 ? 'this person' : `these ${quiet} people`} in this period`}
      </text>);
      y += rowH;
    }
    y += 10;
  }
  const H = y + 10;
  const ticks = calendarTicks(startAt, endAt, x);
  const recentFrom = Math.max(startAt, nowAt - 30 * DAY);
  const label = RANGES.find(item => item.key === range)?.phrase || 'all time';
  return <Box overflowX="auto" {...tipHandlers}>
    <svg viewBox={`0 0 ${W} ${H + 12}`} width="100%" style={{ minWidth: 760, display: 'block' }} role="img"
      aria-label={`Timeline of what ${plural(people.length, 'person', 'people')} did in Pabs over ${label}, grouped by how far into the loop they got`}>
      {days !== 30 && <rect x={x(recentFrom)} y="30" width={Math.max(0, x(nowAt) - x(recentFrom))} height={H - 30} fill="rgba(42,120,214,0.07)" />}
      {ticks.map(tick => <g key={tick.at}>
        <line x1={x(tick.at)} x2={x(tick.at)} y1="30" y2={H} stroke="#EDF2F7" />
        {tick.labelled && <text x={x(tick.at) + 3} y="12" fontSize="11" fill="#718096">{tick.label}</text>}
        {tick.year && <text x={x(tick.at) + 3} y="25" fontSize="10" fill="#A0AEC0">{tick.year}</text>}
      </g>)}
      <line x1={x(nowAt)} x2={x(nowAt)} y1="26" y2={H} stroke="#4A5568" strokeDasharray="3 3" />
      {days !== 30 && <text x={x(nowAt) - 4} y={H + 10} textAnchor="end" fontSize="11" fill="#718096">last 30 days</text>}
      <text x={R + 8} y="12" fontSize="11" fill="#718096">last seen</text>
      {parts}
    </svg>
  </Box>;
}

function Legend() {
  const items = [
    [<circle cx="6" cy="6" r="4" fill={MARK.plan} />, 'Worked on a plan'],
    [<circle cx="6" cy="6" r="5" fill={MARK.returned} />, 'Brought back what happened'],
    [<path d="M6 0L12 6L6 12L0 6Z" fill={MARK.changed} />, 'Changed or confirmed a decision'],
    [<path d="M6 1L11.5 11L0.5 11Z" fill={MARK.build} />, 'Took strategy into a build tool'],
  ];
  return <Flex wrap="wrap" columnGap={5} rowGap={1} fontSize="sm" color="gray.700">
    {items.map(([shape, label]) => <HStack key={label} spacing={2}><svg width="12" height="12" aria-hidden="true">{shape}</svg><Text>{label}</Text></HStack>)}
  </Flex>;
}

function Step({ colour, kicker, children, extra }) {
  return <Box as="li" position="relative" pl={6} pb={3} listStyleType="none"
    _before={{ content: '""', position: 'absolute', left: '4px', top: '6px', w: '9px', h: '9px', borderRadius: 'full', bg: colour }}>
    <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider" color="gray.600">{kicker}</Text>
    <Text fontSize="sm" color="gray.800">{children}</Text>
    {extra && <Text fontSize="sm" color="gray.600" mt={1}>{extra}</Text>}
  </Box>;
}

function Cases({ cases }) {
  if (!cases.length) return <Text fontSize="sm" color="gray.600">No closed loops in this view yet.</Text>;
  return <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={4}>
    {cases.map(item => <Box as="article" key={item.person_id} borderWidth="1px" borderColor="gray.200" borderRadius="md" p={4}
      aria-label={`${item.name}: ${plural(item.loops, 'loop', 'loops')}`}>
      <Flex justify="space-between" align="baseline" gap={2} mb={3}>
        <Text fontWeight="semibold">{item.name}{item.is_admin ? ' · admin' : ''}</Text>
        <Text fontSize="xs" color="gray.600">{plural(item.loops, 'loop', 'loops')} · {formatDate(item.at)}</Text>
      </Flex>
      <Box as="ol" m={0} p={0}>
        <Step colour={MARK.plan} kicker={item.question_label || `Open ${item.question_type}`}>{item.question || 'Not recorded'}</Step>
        <Step colour={MARK.returned} kicker="What they did and brought back">{item.returned}</Step>
        <Step colour={MARK.changed} kicker="Their call">{item.call}</Step>
        <Step colour={MARK.changed} kicker="What changed" extra={item.still_open ? `Still open: ${item.still_open}` : null}>{item.changed}</Step>
      </Box>
      <Text fontSize="xs" color="gray.600">{item.learning_id ? `Learning #${item.learning_id} · ` : ''}via {item.via}</Text>
    </Box>)}
  </SimpleGrid>;
}

export default function AdminBehaviourChangeTab() {
  const [includeAdmins, setIncludeAdmins] = useState(false);
  const [retry, setRetry] = useState(0);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tipHandlers, tip] = useHoverTip();
  const [range, setRange] = useState('all');

  useEffect(() => {
    const controller = new AbortController(); let active = true;
    setLoading(true); setError(null); setReport(null);
    (async () => {
      try {
        const response = await fetchWithAuth(`/api/admin/pilot/behaviour-change?cohort=${includeAdmins ? 'all' : 'pilot'}`, { signal: controller.signal });
        if (!response?.ok) throw new Error('Behaviour change could not load. Try again.');
        const next = await response.json();
        if (!next.answer || !Array.isArray(next.steps) || !Array.isArray(next.people) || !next.value) throw new Error('Behaviour change could not load. Try again.');
        if (active) setReport(next);
      } catch (failure) {
        if (active && failure.name !== 'AbortError') setError(failure.message);
      } finally { if (active) setLoading(false); }
    })();
    return () => { active = false; controller.abort(); };
  }, [includeAdmins, retry]);

  return <Box minW={0} bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="md" p={{ base: 4, md: 6 }}>
    <Flex align="center" justify="space-between" wrap="wrap" gap={3}>
      <Box>
        <Heading as="h2" fontSize="2xl" fontWeight="semibold">Behaviour change</Heading>
        <Text color="gray.600" fontSize="sm" mt={1}>Is Pabs changing how people decide what to build?</Text>
      </Box>
      <HStack minH="44px" spacing={2} flexShrink={0}>
        <Switch id="behaviour-admins" aria-label="Include admins" isChecked={includeAdmins} onChange={event => setIncludeAdmins(event.target.checked)} />
        <Text as="label" htmlFor="behaviour-admins" fontSize="sm" color="gray.700" cursor="pointer">Include admins</Text>
      </HStack>
    </Flex>
    {loading && <Stack role="status" aria-label="Loading behaviour change" spacing={3} mt={5}><Skeleton height="120px" /><Skeleton height="220px" /></Stack>}
    {error && <Alert status="error" mt={5}><AlertIcon /><Box><Text>{error}</Text>
      <Button mt={2} minH="44px" bg="gray.700" color="white" _hover={{ bg: 'gray.600' }} onClick={() => setRetry(value => value + 1)}>Try again</Button>
    </Box></Alert>}
    {!loading && !error && report && <>
      <Answer answer={report.answer} generatedAt={report.scope.generated_at} />
      <Section title="Where people leave the loop" intro="Each dot is one person. The Pabs loop: decide, act, bring back what happened, change the decision because of it, repeat.">
        <Staircase steps={report.steps} people={report.people} tipHandlers={tipHandlers} />
      </Section>
      <Section title="What it's worth" intro="Linking the loop to money and product outcomes. Facts from their records first, then one clearly marked estimate.">
        <Worth value={report.value} />
      </Section>
      <Section title="What each person actually did" intro="Only things a person did. Scheduled Pulses, connector syncs and AI-detected evidence nobody brought back are left out. Green bands mark closed loops.">
        <Flex justify="space-between" align={{ base: 'flex-start', lg: 'center' }} direction={{ base: 'column', lg: 'row' }} gap={3} mb={3}>
          <Legend />
          <RangeSwitch value={range} onChange={setRange} />
        </Flex>
        <Text fontSize="xs" color="gray.600" mb={2}>The range zooms the calendar only. Groups, the staircase and what it's worth use each person's full history.</Text>
        <Timeline people={report.people} generatedAt={report.scope.generated_at} range={range} tipHandlers={tipHandlers} />
      </Section>
      <Section title="The loops, in their own records" intro="One closed loop per person, taken from the saved Learning or strategy change. Nothing here is generated.">
        <Cases cases={report.cases} />
      </Section>
    </>}
    {tip}
  </Box>;
}
